import { useState, useEffect } from "react";
import { Table, Modal, Spin, Button, Card, Row, Col, Input, Tag, Space, Tooltip } from "antd";
import { SettingOutlined, DownloadOutlined, ReloadOutlined, SearchOutlined, BankOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";
import { toast } from "react-toastify";
import { frenchDate } from "../../variables/functions";

const Comptes = () => {
    const [comptes, setComptes] = useState([]);
    const [filteredComptes, setFilteredComptes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalCompte, setModalCompte] = useState(false);
    const [numeroCompteCompagnie, setNumeroCompteCompagnie] = useState("");
    const [selectedCompte, setSelectedCompte] = useState({});
    const [creating, setCreating] = useState(false);
    const [intitule, setIntitule] = useState("");
    const [searchText, setSearchText] = useState("");

    const getOrganismeName = (numeroCompagnie) => {
        switch (numeroCompagnie) {
            case "01": return "Fidélia Assurances";
            case "02": return "NSIA Assurances";
            case "03": return "Sanlam Togo";
            case "04": return "GTA Assurances";
            case "05": return "SUNU Assurances IARD";
            default: return "POOL TPV";
        }
    };

    useEffect(() => {
        loadComptes();
    }, []);

    const loadComptes = () => {
        setLoading(true);
        axiosInstance
            .get("/api/compte/connected")
            .then((response) => {
                const data = response.data.map((item) => ({
                    key: item.codeId,
                    numeroCompte: `${item.numeroCompte}.${item.numeroCompagnie}.${item.numeroTiers}`,
                    numeroCompteCompagnie: item.numeroCompteCompagnie || "-",
                    nomCompte: item.libelle,
                    organisme: getOrganismeName(item.numeroCompagnie),
                    dateCreation: frenchDate(item.createdAt),
                    data: item,
                }));
                setComptes(data);
                setFilteredComptes(data);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
                toast.error("Erreur lors du chargement des comptes");
            });
    };

    const getOrganismeColor = (organisme) => {
        const colors = {
            "Fidélia Assurances": "#F39C12",
            "NSIA Assurances": "#A66E00",
            "Sanlam Togo": "#F39C12",
            "GTA Assurances": "#A66E00",
            "SUNU Assurances IARD": "#F39C12",
            "POOL TPV": "#A66E00"
        };
        return colors[organisme] || "#F39C12";
    };

    const handleSearch = (value) => {
        setSearchText(value);
        const filtered = comptes?.filter(compte =>
            compte?.numeroCompte?.toLowerCase().includes(value?.toLowerCase()) ||
            compte?.numeroCompteCompagnie?.toLowerCase().includes(value?.toLowerCase()) ||
            compte?.nomCompte?.toLowerCase().includes(value?.toLowerCase()) ||
            compte?.organisme?.toLowerCase().includes(value?.toLowerCase())
        );
        setFilteredComptes(filtered);
    };

    const createFrom = () => {
        setCreating(true);
    };

    const updateCompte = (account) => {
        if (!numeroCompteCompagnie) {
            toast("Veuillez renseigner le numéro de compte compagnie!");
            return;
        }
        setLoading(true);
        const updatedAccount = {
            ...account,
            numeroCompteCompagnie: numeroCompteCompagnie,
        };

        axiosInstance
            .put("/api/compte", updatedAccount)
            .then(() => {
                setModalCompte(false);
                setLoading(false);
                toast.success("Compte mis à jour avec succès!");
                loadComptes();
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
                toast.error("Erreur lors de la mise à jour du compte");
            });
    };

    const exportComptes = () => {
        if (comptes.length === 0) {
            toast.warning("Aucun compte à exporter !");
            return;
        }

        const worksheetData = comptes.map((c) => ({
            "Numéro de compte POOL": c.numeroCompte,
            "Numéro de compte compagnie": c.numeroCompteCompagnie,
            "Nom du compte": c.nomCompte,
            "Organisme": c.organisme,
            "Date création": c.dateCreation,
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Comptes");
        XLSX.writeFile(workbook, `comptes_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const regenerate = () => {
        setLoading(true);
        axiosInstance
            .get(`/api/compte/regenerate`)
            .then(() => {
                setLoading(false);
                toast.success("Comptes mis à jour avec succès!");
                loadComptes();
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
                toast.error("Erreur lors de la mise à jour des comptes");
            });
    };

    const columns = [
        {
            title: "Numéro POOL",
            dataIndex: "numeroCompte",
            key: "numeroCompte",
            render: (text) => <span style={{ fontWeight: 600, color: "#2D2D2D" }}>{text}</span>
        },
        {
            title: "Numéro Compagnie",
            dataIndex: "numeroCompteCompagnie",
            key: "numeroCompteCompagnie",
            render: (text) => <span style={{ fontFamily: 'monospace', color: "#A66E00" }}>{text}</span>
        },
        {
            title: "Nom du compte",
            dataIndex: "nomCompte",
            key: "nomCompte",
            render: (text) => <span style={{ color: "#2D2D2D" }}>{text}</span>
        },
        {
            title: "Organisme",
            dataIndex: "organisme",
            key: "organisme",
            render: (text) => (
                <Tag color={getOrganismeColor(text)} style={{
                    borderRadius: '6px',
                    padding: '4px 12px',
                    fontWeight: 500,
                    border: 'none'
                }}>
                    {text}
                </Tag>
            )
        },
        {
            title: "Date création",
            dataIndex: "dateCreation",
            key: "dateCreation",
            render: (text) => <span style={{ color: "#666" }}>{text}</span>
        },
        {
            title: "Actions",
            dataIndex: "actions",
            key: "actions",
            render: (_, record) => (
                <Tooltip title="Modifier le compte">
                    <Button
                        type="primary"
                        icon={<SettingOutlined />}
                        onClick={() => {
                            setSelectedCompte(record.data);
                            setNumeroCompteCompagnie(record?.data?.numeroCompteCompagnie || "");
                            setCreating(false);
                            setModalCompte(true);
                        }}
                        style={{
                            backgroundColor: "#F39C12",
                            borderColor: "#F39C12",
                            borderRadius: "8px",
                            fontWeight: 500,
                            boxShadow: "0 2px 8px rgba(243, 156, 18, 0.2)"
                        }}
                    >
                        Modifier
                    </Button>
                </Tooltip>
            )
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"COMPTABILITE"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div style={{
                            minHeight: '100vh',
                            backgroundColor: '#F2F0ED',
                            padding: '24px'
                        }}>
                            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                                {/* En-tête */}
                                <div style={{
                                    marginBottom: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px'
                                }}>
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '12px',
                                        backgroundColor: '#F39C12',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(243, 156, 18, 0.3)'
                                    }}>
                                        <BankOutlined style={{ fontSize: '24px', color: 'white' }} />
                                    </div>
                                    <div>
                                        <h1 style={{
                                            margin: 0,
                                            fontSize: '28px',
                                            fontWeight: 700,
                                            color: '#2D2D2D'
                                        }}>
                                            Comptes Comptables
                                        </h1>
                                        <p style={{
                                            margin: 0,
                                            color: '#666',
                                            fontSize: '14px'
                                        }}>
                                            Gestion et suivi des comptes organismes
                                        </p>
                                    </div>
                                </div>

                                {/* Barre de recherche et actions */}
                                <Card style={{
                                    marginBottom: '24px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                }}>
                                    <Row gutter={[16, 16]} align="middle">
                                        <Col xs={24} md={12}>
                                            <Input
                                                placeholder="Rechercher un compte..."
                                                prefix={<SearchOutlined style={{ color: '#F39C12' }} />}
                                                value={searchText}
                                                onChange={(e) => handleSearch(e.target.value)}
                                                size="large"
                                                style={{
                                                    borderRadius: '8px',
                                                    borderColor: '#FFF3E0'
                                                }}
                                            />
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Space style={{ float: 'right' }}>
                                                <Button
                                                    icon={<DownloadOutlined />}
                                                    onClick={exportComptes}
                                                    size="large"
                                                    style={{
                                                        borderRadius: '8px',
                                                        borderColor: '#F39C12',
                                                        color: '#F39C12',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    Exporter
                                                </Button>
                                                <Button
                                                    loading={loading}
                                                    icon={<ReloadOutlined />}
                                                    onClick={regenerate}
                                                    size="large"
                                                    style={{
                                                        backgroundColor: '#A66E00',
                                                        borderColor: '#A66E00',
                                                        color: 'white',
                                                        borderRadius: '8px',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    Régénérer
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Tableau */}
                                <Card style={{
                                    borderRadius: '12px',
                                    border: 'none',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                    overflow: 'hidden'
                                }}>
                                    <Spin spinning={loading}>
                                        <Table
                                            columns={columns}
                                            dataSource={filteredComptes}
                                            rowKey="key"
                                            pagination={{
                                                pageSize: 10,
                                                showSizeChanger: true,
                                                showTotal: (total) => `Total: ${total} comptes`
                                            }}
                                            style={{
                                                '--ant-table-header-bg': '#FFF3E0',
                                            }}
                                        />
                                    </Spin>
                                </Card>

                                {/* Modal */}
                                <Modal
                                    title={
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            color: '#2D2D2D',
                                            fontSize: '20px',
                                            fontWeight: 600
                                        }}>
                                            <div style={{
                                                width: '36px',
                                                height: '36px',
                                                borderRadius: '8px',
                                                backgroundColor: '#FFF3E0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <SettingOutlined style={{ color: '#F39C12', fontSize: '18px' }} />
                                            </div>
                                            {creating ? "Création de compte" : "Modification de compte"}
                                        </div>
                                    }
                                    open={modalCompte}
                                    onCancel={() => {
                                        setModalCompte(false);
                                        setCreating(false);
                                        setIntitule("");
                                    }}
                                    footer={
                                        creating ? [
                                            <Button
                                                key="cancel"
                                                onClick={() => {
                                                    setModalCompte(false);
                                                    setCreating(false);
                                                }}
                                                style={{ borderRadius: '8px' }}
                                            >
                                                Annuler
                                            </Button>,
                                            <Button
                                                key="create"
                                                type="primary"
                                                onClick={() => updateCompte(selectedCompte)}
                                                style={{
                                                    backgroundColor: '#F39C12',
                                                    borderColor: '#F39C12',
                                                    borderRadius: '8px',
                                                    fontWeight: 500
                                                }}
                                            >
                                                Créer
                                            </Button>
                                        ] : [
                                            <Button
                                                key="cancel"
                                                onClick={() => setModalCompte(false)}
                                                style={{ borderRadius: '8px' }}
                                            >
                                                Annuler
                                            </Button>,
                                            <Button
                                                key="update"
                                                type="primary"
                                                onClick={() => updateCompte(selectedCompte)}
                                                style={{
                                                    backgroundColor: '#A66E00',
                                                    borderColor: '#A66E00',
                                                    borderRadius: '8px',
                                                    fontWeight: 500
                                                }}
                                            >
                                                Modifier
                                            </Button>,
                                            <Button
                                                key="createFrom"
                                                type="primary"
                                                onClick={() => createFrom()}
                                                style={{
                                                    backgroundColor: '#F39C12',
                                                    borderColor: '#F39C12',
                                                    borderRadius: '8px',
                                                    fontWeight: 500
                                                }}
                                            >
                                                Créer à partir
                                            </Button>
                                        ]
                                    }
                                    width={600}
                                    style={{ top: 20 }}
                                >
                                    <div style={{ padding: '24px 0' }}>
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: '8px',
                                                color: '#2D2D2D',
                                                fontWeight: 500,
                                                fontSize: '14px'
                                            }}>
                                                Numéro de compte - POOL
                                            </label>
                                            <Input
                                                disabled
                                                value={`${selectedCompte.numeroCompte || ''}.${selectedCompte.numeroCompagnie || ''}`}
                                                style={{
                                                    backgroundColor: '#F2F0ED',
                                                    borderRadius: '8px',
                                                    borderColor: '#FFF3E0'
                                                }}
                                                size="large"
                                            />
                                        </div>

                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: '8px',
                                                color: '#2D2D2D',
                                                fontWeight: 500,
                                                fontSize: '14px'
                                            }}>
                                                Numéro de compte de la compagnie *
                                            </label>
                                            <Input
                                                placeholder="Ex: FID-2024-001"
                                                value={numeroCompteCompagnie}
                                                onChange={(e) => setNumeroCompteCompagnie(e.target.value)}
                                                style={{
                                                    borderRadius: '8px',
                                                    borderColor: '#FFF3E0'
                                                }}
                                                size="large"
                                            />
                                        </div>

                                        {creating && (
                                            <div style={{ marginBottom: '20px' }}>
                                                <label style={{
                                                    display: 'block',
                                                    marginBottom: '8px',
                                                    color: '#2D2D2D',
                                                    fontWeight: 500,
                                                    fontSize: '14px'
                                                }}>
                                                    Intitulé *
                                                </label>
                                                <Input
                                                    placeholder="Saisir l'intitulé du compte"
                                                    value={intitule}
                                                    onChange={(e) => setIntitule(e.target.value)}
                                                    style={{
                                                        borderRadius: '8px',
                                                        borderColor: '#FFF3E0'
                                                    }}
                                                    size="large"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </Modal>
                            </div>
                        </div>{/* Footer */}
                        <Footer />
                        {/* /Footer */}

                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
                {/* /Content wrapper */}
            </div>
            {/* /Layout page */}
            {/* Overlay */}
            <div className="layout-overlay layout-menu-toggle show"></div>
        </div>
    );
};

export default Comptes;